import Icon from "@react-native-vector-icons/material-design-icons/static";
import { useState } from "react";
import { TouchableOpacity } from "react-native";
import {
	Appbar,
	Button,
	Dialog,
	Divider,
	Text,
	TextInput,
} from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";
import DialogWrapper from "../DialogWrapper";
import ScrollContainer from "../ScrollContainer";
type TextInputConfig = {
	id: string;
	label: string;
	placeholder: string;
	multiline?: boolean;
	numberOfLines?: number;
	helperText?: string;
};

type Tag = {
	text: string;
	onPress: () => void;
};
type CustomizeDialogProps = {
	items: (TextInputConfig | Tag[])[];
	onBackPress: () => void;
	onSubmit: (inputValues: { [key: string]: string }) => void;
	isWeb?: boolean;
};
const CustomizeAssistantDialog = (props: CustomizeDialogProps) => {
	const theme = useAppTheme();
	const [inputValues, setInputValues] = useState<{ [key: string]: string }>({});

	const handleInputChange = (id: string, text: string) => {
		setInputValues((prev) => ({ ...prev, [id]: text }));
	};

	const handleSubmit = () => {
		// Filter out empty values (empty strings, null, undefined)
		const filteredValues = Object.entries(inputValues).reduce(
			(acc, [key, value]) => {
				if (value && value.trim().length > 0) {
					acc[key] = value;
				}
				return acc;
			},
			{} as { [key: string]: string },
		);
		props.onSubmit(filteredValues);
	};

	const hasValidValues = Object.values(inputValues).some(
		(value) => value && value.trim().length > 0,
	);

	const content = (
		<>
			<Box padding={16} paddingTop={20}>
				<Text
					variant="titleSmall"
					style={{ color: theme.colors.onSurfaceVariant }}
				>
					Responses
				</Text>
			</Box>
			<ScrollContainer style={{ flex: 1 }}>
				<Box gap={16} paddingBottom={8}>
					{props.items.map((item: TextInputConfig | Tag[]) => {
						if (Array.isArray(item)) {
							return (
								<ScrollContainer
									style={{
										flexDirection: "row",
										paddingVertical: 8,
										paddingHorizontal: 16,
										paddingTop: 12,
									}}
									horizontal={true}
									showsHorizontalScrollIndicator={false}
									containerProps={{
										containerStyle: {
											flexDirection: "row",
											gap: 8,
										},
									}}
								>
									{item.map((tag) => (
										<TouchableOpacity key={tag.text} onPress={tag.onPress}>
											<Box
												alignSelf="flex-start"
												flexDirection="row"
												borderRadius={8}
												borderWidth={1}
												borderColor={theme.colors.outlineVariant}
												gap={8}
												paddingLeft={8}
												paddingVertical={6}
												alignItems="center"
												paddingRight={16}
											>
												<Icon
													name="plus"
													color={theme.colors.primary}
													size={18}
												/>
												<Text
													variant="labelLarge"
													style={{ color: theme.colors.onSurface }}
												>
													{tag.text}
												</Text>
											</Box>
										</TouchableOpacity>
									))}
								</ScrollContainer>
							);
						}
						return (
							<Box key={item.id} paddingHorizontal={16}>
								<TextInput
									mode="outlined"
									label={item.label}
									placeholder={item.placeholder}
									value={inputValues[item.id] || ""}
									onChangeText={(text) => handleInputChange(item.id, text)}
									multiline={item.multiline}
									numberOfLines={item.numberOfLines}
								/>
								{item.helperText && (
									<Box paddingTop={4} paddingHorizontal={16}>
										<Text
											variant="bodySmall"
											style={{ color: theme.colors.onSurfaceVariant }}
										>
											{item.helperText}
										</Text>
									</Box>
								)}
							</Box>
						);
					})}
				</Box>
			</ScrollContainer>
		</>
	);

	if (props.isWeb) {
		return (
			<DialogWrapper visible={true} handleClose={props.onBackPress}>
				<Dialog.Title>Customize ACME AI</Dialog.Title>
				<Divider />
				{content}
				<Dialog.Actions>
					<Button mode="text" onPress={() => props.onBackPress()}>
						Cancel
					</Button>
					<Button
						disabled={!hasValidValues}
						mode="contained"
						onPress={handleSubmit}
						style={{
							backgroundColor: hasValidValues
								? theme.colors.primary
								: theme.colors.outline,
							opacity: hasValidValues ? 1 : 0.35,
						}}
					>
						Save
					</Button>
				</Dialog.Actions>
			</DialogWrapper>
		);
	}

	return (
		<Box flex={1} backgroundColor={theme.colors.surface}>
			<Appbar.Header
				style={{
					backgroundColor: theme.colors.surfaceVariant,
				}}
			>
				<Appbar.BackAction onPress={props.onBackPress} />
				<Appbar.Content
					style={{
						alignItems: "center",
					}}
					title="Customize ACME AI"
				/>
				<Appbar.Action
					icon="check"
					onPress={handleSubmit}
					disabled={!hasValidValues}
				/>
			</Appbar.Header>
			<Box flex={1} backgroundColor={theme.colors.surface}>
				{content}
			</Box>
		</Box>
	);
};

export default CustomizeAssistantDialog;
export type { CustomizeDialogProps, TextInputConfig };
